/**
 * 横屏 rem 修正（HotTable 横屏编辑的配套补丁）。
 *
 * 背景问题：用户反馈"手机横过来以后，页面/表格整体被拉伸变形"。
 *
 * 根因：本项目用 amfe-flexible + postcss-pxtorem（rootValue 37.5）做 rem 适配：
 *   - amfe-flexible 在每次 resize / orientationchange 后执行
 *     `documentElement.style.fontSize = clientWidth / 10 + 'px'`；
 *   - postcss-pxtorem 在构建期把所有 CSS 的 px 按 37.5 换算成 rem。
 * 竖屏（375 宽）时 1rem = 37.5px，一切正常；手机转成横屏后 clientWidth
 * 变成 ~812，1rem 翻倍到 81.2px，所有 rem 布局瞬间放大 2.17 倍 ——
 * 页面文字、间距、组件尺寸全部"变形"，表格自然也跟着被拉坏。
 *
 * 修复思路：rem 的语义就是"跟随视口宽度的缩放单位"，横屏把它当"放大镜"
 * 用是错的。这里在 amfe-flexible 设置根字号之后立即把根字号钳回
 * 竖屏基准（按"较短边 / 10"计算），保证旋转前后 1rem 的物理大小一致，
 * 页面只是多了水平空间，不会被整体放大。
 *
 * 实现细节：
 *   1. amfe-flexible 把 setRemUnit 绑在 window resize 上（orientationchange
 *      也会触发 resize），本模块同样监听 resize，但用微任务（queueMicrotask
 *      + 兜底 setTimeout 0）保证一定在 flexible 之后执行，覆盖其结果；
 *   2. 钳制基准取 min(innerWidth, innerHeight) / 10 —— 也就是"较短边 / 10"，
 *      竖屏时与 flexible 的原值相同（行为不变），横屏时即竖屏宽度 / 10；
 *   3. 只钳制"移动设备范围的横屏"（较短边 <= 768px），PC 大屏 / 平板竖屏
 *      不介入，避免影响桌面端调试与大屏展示；
 *   4. 只写 fontSize，不碰 body 的字号与 dpr 逻辑，影响面最小；
 *   5. 该修正与是否点击「横屏编辑」无关：用户手动把手机转成横屏时，
 *      flexible 同样会按横屏宽度放大根字号（这正是截图反馈的变形场景），
 *      因此钳制按全局 resize 兜住所有转屏路径。
 *
 * 为什么不改 amfe-flexible 源码 / 不换 vw 方案：
 *   - vendor 化的第三方包不便改动（升级会丢补丁）；
 *   - 全量换 vw 需要重新回归整个 App 的样式，风险远大于本模块。
 *
 * 注意：本模块是全局性修正（作用于 documentElement），影响整个页面的 rem
 * 布局，不止 HotTable。这正是期望的行为 —— 横屏放大是全局问题。
 */

/**
 * 横屏短边上限（px）：只有较短边 <= 该值的横屏才钳制。
 * 手机横屏的较短边（即竖屏宽度）为 320~480，平板横屏为 768；
 * PC / 大屏显示器横屏的较短边普遍 >= 900，不参与钳制 ——
 * 否则会连带改变 PC 大屏预览时的 rem 缩放，属于额外副作用。
 */
const LANDSCAPE_SHORT_EDGE_MAX = 768;

/** 计算钳制后的根字号：物理短边 / 10，与 pxtorem rootValue 37.5 的设计稿语义对齐 */
function getClampedRemPx() {
  if (typeof screen === 'undefined' || !screen.width || !screen.height) return null;
  // 物理（screen）短边 / 10：375 宽的手机横屏后 = 375 / 10 = 37.5px，与竖屏一致
  return Math.min(screen.width, screen.height) / 10;
}

/**
 * 修正后的方向判定：
 * - 旧实现用 innerWidth > innerHeight 判横屏，存在两类误判：
 *   1. 手机竖屏 + 键盘弹起：视觉视口高度被压缩到 300~400px，
 *      innerWidth(375) > innerHeight(320) 成立，竖屏被当成横屏钳制，
 *      根字号被错误缩到 32px，整页 rem 布局瞬间错乱 —— 这是
 *      /todo-detail-new 页面样式崩坏的直接原因；
 *   2. 笔记本桌面浏览器窗口（如 1366x660）也满足宽>高，被误当
 *      "手机横屏"钳制。
 * - 新实现改用 screen.orientation（物理方向）判定，键盘/地址栏等
 *   视口高度变化不再影响判定；老 WebView 无该 API 时回退到
 *   screen.width/height（物理分辨率，同样不受键盘影响）。
 */
function isPhysicalLandscape() {
  if (typeof screen !== 'undefined' && screen.orientation && typeof screen.orientation.type === 'string') {
    return screen.orientation.type.startsWith('landscape');
  }
  if (typeof screen !== 'undefined' && screen.width && screen.height) {
    return screen.width > screen.height;
  }
  return false;
}

/**
 * 是否处于"需要钳制的横屏"：
 * 物理横屏 且 短边在移动设备范围内（<= 768）。
 * 竖屏（含键盘弹起）与 PC 大屏横屏均不介入。
 */
function needsClamp() {
  const w = window.innerWidth || 0;
  const h = window.innerHeight || 0;
  if (!w || !h) return false;
  const shortEdge = Math.min(screen.width || w, screen.height || h);
  return isPhysicalLandscape() && shortEdge <= LANDSCAPE_SHORT_EDGE_MAX;
}

/** 是否处于"横屏且根字号被本模块钳制"的状态 */
function isLandscapeRemActive() {
  return needsClamp();
}

/** 在 amfe-flexible 的 setRemUnit 之后覆盖根字号（仅移动端横屏时） */
function clampRemUnit() {
  if (!needsClamp()) return;
  const remPx = getClampedRemPx();
  if (remPx === null) return;
  const docEl = document.documentElement;
  // 保留两位小数与 flexible 的写法一致（flexible 不四舍五入，这里也不做）
  docEl.style.fontSize = `${remPx}px`;
}

let installed = false;
let pending = false;

/**
 * 用微任务 + 宏任务双保险执行钳制：
 * amfe-flexible 的 resize 监听是同步回调且先于本模块注册，resize 事件按
 * 注册顺序同步派发，因此必须放到事件循环更晚的节点才能覆盖它的结果。
 * pending 标记保证微任务与宏任务只执行第一次（后到的直接跳过）。
 */
function scheduleClamp() {
  if (pending) return;
  pending = true;
  const run = () => {
    if (!pending) return;
    pending = false;
    clampRemUnit();
  };
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(run);
  }
  setTimeout(run, 0);
}

/**
 * 安装横屏 rem 修正。
 * 幂等：重复调用不会重复注册监听。
 */
export function installLandscapeRemFix() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // resize（含 orientationchange 引发的）后钳制
  window.addEventListener('resize', scheduleClamp);
  // 部分 WebView 上 orientationchange 不派发 resize，单独兜底
  window.addEventListener('orientationchange', scheduleClamp);
  // pageshow（bfcache 恢复）时 flexible 会重设根字号，跟随钳制
  window.addEventListener('pageshow', scheduleClamp);

  // 首次安装时立即执行一次（处理"已处于横屏才打开页面"的情况）
  scheduleClamp();
}

/** 卸载（一般不需要，保留给测试 / 特殊场景） */
export function uninstallLandscapeRemFix() {
  if (!installed) return;
  installed = false;
  window.removeEventListener('resize', scheduleClamp);
  window.removeEventListener('orientationchange', scheduleClamp);
  window.removeEventListener('pageshow', scheduleClamp);
}

export { isLandscapeRemActive };
